import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Calendar, Filter, X, Receipt, 
  TrendingUp, BarChart3, FolderPlus, Tag, List
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colors for charts
const COLORS = ['#f97316', '#3b82f6', '#22c55e', '#eab308', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

export default function GeneralExpenses() {
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteCategoryConfirm, setDeleteCategoryConfirm] = useState(null);
  
  // Form state
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split('T')[0],
    category_id: '',
    description: '',
    items: []  // New: array of line items
  });
  
  // New line item template
  const emptyLineItem = { description: '', quantity: 1, unit_price: 0 };
  const [newLineItem, setNewLineItem] = useState({ ...emptyLineItem });
  
  // Calculate total from items
  const calculateItemsTotal = (items) => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  // Set default date range to current month
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  // Fetch data
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchExpenses();
      fetchStats();
    }
  }, [startDate, endDate, filterCategory]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/expense-categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (filterCategory && filterCategory !== 'all') params.append('category_id', filterCategory);
      
      const res = await axios.get(`${API}/general-expenses?${params}`);
      setExpenses(res.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (filterCategory && filterCategory !== 'all') params.append('category_id', filterCategory);
      
      const res = await axios.get(`${API}/general-expenses/stats?${params}`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Category handlers
  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    
    try {
      await axios.post(`${API}/expense-categories`, newCategory);
      toast.success('Categoría creada');
      setNewCategory({ name: '', description: '' });
      setShowCategoryModal(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear categoría');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await axios.delete(`${API}/expense-categories/${categoryId}`);
      toast.success('Categoría eliminada');
      fetchCategories();
      setDeleteCategoryConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
      setDeleteCategoryConfirm(null);
    }
  };

  // Expense handlers
  const handleOpenExpenseModal = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setExpenseForm({
        date: expense.date,
        category_id: expense.category_id || '',
        description: expense.description || '',
        items: expense.items || []
      });
    } else {
      setEditingExpense(null);
      setExpenseForm({
        date: new Date().toISOString().split('T')[0],
        category_id: '',
        description: '',
        items: []
      });
    }
    setNewLineItem({ ...emptyLineItem });
    setShowExpenseModal(true);
  };

  // Line item handlers
  const handleAddLineItem = () => {
    if (!newLineItem.description.trim()) {
      toast.error('La descripción es obligatoria');
      return;
    }
    if (newLineItem.unit_price <= 0) {
      toast.error('El precio debe ser mayor que 0');
      return;
    }
    
    const itemWithTotal = {
      ...newLineItem,
      total: round(newLineItem.quantity * newLineItem.unit_price, 2)
    };
    
    setExpenseForm({
      ...expenseForm,
      items: [...expenseForm.items, itemWithTotal]
    });
    setNewLineItem({ ...emptyLineItem });
  };

  const handleRemoveLineItem = (index) => {
    setExpenseForm({
      ...expenseForm,
      items: expenseForm.items.filter((_, i) => i !== index)
    });
  };

  const handleSaveExpense = async () => {
    // Category is now optional
    if (expenseForm.items.length === 0) {
      toast.error('Añade al menos una línea de gasto');
      return;
    }
    
    const totalPrice = calculateItemsTotal(expenseForm.items);
    
    const data = {
      date: expenseForm.date,
      category_id: expenseForm.category_id || null,
      description: expenseForm.description,
      items: expenseForm.items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price
      })),
      total_price: round(totalPrice, 2)
    };
    
    try {
      if (editingExpense) {
        await axios.put(`${API}/general-expenses/${editingExpense.id}`, data);
        toast.success('Gasto actualizado');
      } else {
        await axios.post(`${API}/general-expenses`, data);
        toast.success('Gasto registrado');
      }
      setShowExpenseModal(false);
      fetchExpenses();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar gasto');
    }
  };

  // Helper function for rounding
  const round = (num, decimals) => {
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/general-expenses/${expenseId}`);
      toast.success('Gasto eliminado');
      fetchExpenses();
      fetchStats();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
      setDeleteConfirm(null);
    }
  };

  // Format helpers
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES');
  };

  // Pie chart data
  const pieData = useMemo(() => {
    if (!stats?.by_category) return [];
    return stats.by_category.map((cat, idx) => ({
      name: cat.category_name,
      value: cat.total,
      color: COLORS[idx % COLORS.length]
    }));
  }, [stats]);

  return (
    <div className="space-y-6" data-testid="general-expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gastos Generales</h1>
          <p className="text-slate-500 text-sm">Control de gastos internos del negocio</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowCategoryModal(true)}
            data-testid="manage-categories-btn"
          >
            <Tag className="w-4 h-4 mr-2" />
            Categorías
          </Button>
          <Button 
            onClick={() => handleOpenExpenseModal()}
            data-testid="add-expense-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Gasto
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Desde</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Hasta</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
                data-testid="filter-end-date"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Categoría</label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-48" data-testid="filter-category">
                  <SelectValue placeholder="Todas las categorías" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {filterCategory && filterCategory !== 'all' && (
              <Button variant="ghost" size="sm" onClick={() => setFilterCategory('all')}>
                <X className="w-4 h-4 mr-1" />
                Limpiar filtro
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Gastado</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.total_amount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Número de Gastos</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Media por Gasto</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {stats.total_count > 0 ? formatCurrency(stats.total_amount / stats.total_count) : '0 €'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      {stats && stats.by_category.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar Chart by Category */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Gastos por Categoría</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.by_category} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => `${v}€`} />
                  <YAxis type="category" dataKey="category_name" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="total" fill="#f97316" radius={[0, 4, 4, 0]}>
                    {stats.by_category.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Distribución por Categoría</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Listado de Gastos</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner w-8 h-8" />
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No hay gastos en el período seleccionado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Fecha</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Categoría</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-500">Descripción</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-slate-500">Cantidad</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-slate-500">Total</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-slate-500">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map(expense => (
                    <tr key={expense.id} className="border-b border-slate-100 hover:bg-slate-50 align-top">
                      <td className="py-3 px-2 text-sm">{formatDate(expense.date)}</td>
                      <td className="py-3 px-2">
                        {expense.category_name ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                            {expense.category_name}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Sin categoría</span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <div className="text-sm font-medium text-slate-700">{expense.description || '-'}</div>
                        {/* Show line items if present */}
                        {expense.items && expense.items.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {expense.items.map((item, idx) => (
                              <div key={idx} className="text-xs text-slate-500 flex justify-between">
                                <span>{item.description} ({item.quantity} × {formatCurrency(item.unit_price)})</span>
                                <span className="font-medium ml-2">{formatCurrency(item.quantity * item.unit_price)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm text-right text-slate-500">
                        {expense.items?.length > 0 ? (
                          <span className="text-xs">{expense.items.length} líneas</span>
                        ) : (
                          expense.quantity || '-'
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm text-right font-medium text-green-700">{formatCurrency(expense.total_price)}</td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenExpenseModal(expense)}
                            data-testid={`edit-expense-${expense.id}`}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteConfirm(expense)}
                            data-testid={`delete-expense-${expense.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50">
                    <td colSpan="4" className="py-3 px-2 text-sm font-medium text-right">Total:</td>
                    <td className="py-3 px-2 text-sm text-right font-bold text-orange-600">
                      {formatCurrency(expenses.reduce((sum, e) => sum + e.total_price, 0))}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category Management Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Gestionar Categorías</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Create new category */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <h4 className="font-medium text-sm">Nueva Categoría</h4>
              <Input
                placeholder="Nombre de la categoría"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                data-testid="new-category-name"
              />
              <Input
                placeholder="Descripción (opcional)"
                value={newCategory.description}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                data-testid="new-category-description"
              />
              <Button onClick={handleCreateCategory} className="w-full" data-testid="create-category-btn">
                <FolderPlus className="w-4 h-4 mr-2" />
                Crear Categoría
              </Button>
            </div>
            
            {/* List existing categories */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Categorías Existentes</h4>
              {categories.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No hay categorías creadas</p>
              ) : (
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {categories.map(cat => (
                    <div key={cat.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{cat.name}</p>
                        {cat.description && <p className="text-xs text-slate-500">{cat.description}</p>}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteCategoryConfirm(cat)}
                        data-testid={`delete-category-${cat.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Expense Modal */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-green-600" />
              {editingExpense ? 'Editar Gasto' : 'Nuevo Gasto'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Date and Category row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium">Fecha *</label>
                <Input
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                  data-testid="expense-date"
                />
              </div>
              
              <div className="space-y-1">
                <label className="text-sm font-medium">Categoría (opcional)</label>
                <Select 
                  value={expenseForm.category_id || "none"} 
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, category_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="expense-category">
                    <SelectValue placeholder="Sin categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin categoría</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Description/Title */}
            <div className="space-y-1">
              <label className="text-sm font-medium">Título / Descripción general</label>
              <Input
                placeholder="Ej: Compra en Ferretería"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                data-testid="expense-description"
              />
            </div>
            
            {/* Line Items Section */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold flex items-center gap-2">
                  <List className="w-4 h-4" />
                  Líneas de gasto
                </label>
                <span className="text-xs text-slate-500">
                  {expenseForm.items.length} {expenseForm.items.length === 1 ? 'línea' : 'líneas'}
                </span>
              </div>
              
              {/* Existing line items */}
              {expenseForm.items.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {expenseForm.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.description}</p>
                        <p className="text-xs text-slate-500">
                          {item.quantity} × {formatCurrency(item.unit_price)}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-semibold text-green-700">{formatCurrency(item.quantity * item.unit_price)}</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveLineItem(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new line item */}
              <div className="flex gap-2 items-end p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex-1">
                  <label className="text-xs text-blue-700">Descripción *</label>
                  <Input
                    value={newLineItem.description}
                    onChange={(e) => setNewLineItem({ ...newLineItem, description: e.target.value })}
                    placeholder="Ej: Tornillos, Spray, Bridas..."
                    className="h-9"
                    data-testid="line-item-description"
                  />
                </div>
                <div className="w-20">
                  <label className="text-xs text-blue-700">Cantidad</label>
                  <Input
                    type="number"
                    step="any"
                    min="0.01"
                    value={newLineItem.quantity}
                    onChange={(e) => setNewLineItem({ ...newLineItem, quantity: parseFloat(e.target.value) || 0 })}
                    className="h-9"
                    data-testid="line-item-quantity"
                  />
                </div>
                <div className="w-24">
                  <label className="text-xs text-blue-700">P. Unitario</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newLineItem.unit_price}
                    onChange={(e) => setNewLineItem({ ...newLineItem, unit_price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    className="h-9"
                    data-testid="line-item-unit-price"
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleAddLineItem}
                  className="bg-blue-600 hover:bg-blue-700 h-9"
                  data-testid="add-line-item-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Añade cada producto o concepto como una línea separada
              </p>
            </div>
            
            {/* Total */}
            {expenseForm.items.length > 0 && (
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <span className="font-semibold text-green-800">TOTAL</span>
                <span className="text-2xl font-bold text-green-700">
                  {formatCurrency(calculateItemsTotal(expenseForm.items))}
                </span>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExpenseModal(false)}>Cancelar</Button>
            <Button 
              onClick={handleSaveExpense} 
              disabled={expenseForm.items.length === 0}
              data-testid="save-expense-btn"
            >
              {editingExpense ? 'Guardar Cambios' : 'Registrar Gasto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Expense Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el registro de {deleteConfirm?.description || 'este gasto'} por {deleteConfirm && formatCurrency(deleteConfirm.total_price)}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDeleteExpense(deleteConfirm?.id)}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Category Confirmation */}
      <AlertDialog open={!!deleteCategoryConfirm} onOpenChange={() => setDeleteCategoryConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará la categoría "{deleteCategoryConfirm?.name}". Solo se puede eliminar si no tiene gastos asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDeleteCategory(deleteCategoryConfirm?.id)}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
